import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import AlertComponent from "../../components/AlertComponent";

function formatarChatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "America/Sao_Paulo",
  });
}

export default function TelegramChatPickerModal({ currentUserId, currentChatId, onSelect, onClose }) {
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChatId, setSelectedChatId] = useState(currentChatId || null);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API}/admin/users/telegram/chats`)
      .then((res) => setChats(res.data || []))
      .catch((err) => {
        const mensagem = err?.response?.data?.message || err?.message || "Erro ao carregar chats";
        AlertComponent("Erro", mensagem, { icon: "error" });
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const chatsDisponiveis = useMemo(() => {
    return chats.filter((c) => {
      if (!c.isLinked) return true;
      if (currentUserId != null && (c.linkedUsers || []).some((u) => u.id === currentUserId)) return true;
      return false;
    });
  }, [chats, currentUserId]);

  const handleConfirm = () => {
    const chat = chatsDisponiveis.find((c) => String(c.chatId) === String(selectedChatId));
    if (!chat) return;
    onSelect(chat);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl ring-1 ring-slate-200/60 w-full max-w-lg flex flex-col overflow-hidden"
        style={{ maxHeight: "min(85vh, 720px)" }}
      >
        {/* Header */}
        <div
          className="px-6 py-5 relative overflow-hidden text-white shrink-0"
          style={{ background: "linear-gradient(135deg, #1e1b4b 0%, #3b0764 100%)" }}
        >
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-10" style={{ background: "radial-gradient(circle,#a78bfa,transparent)" }} />
          <div className="relative flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-purple-300 text-[11px] font-semibold uppercase tracking-wider mb-1">Telegram</p>
              <h2 className="text-lg font-bold">Selecionar chat</h2>
              <p className="text-purple-200 text-xs mt-0.5">Chats que ainda não estão vinculados a nenhum usuário (ou ao próprio).</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Fechar"
              className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-2 border-violet-300 border-t-violet-600 rounded-full animate-spin" />
            </div>
          ) : chatsDisponiveis.length === 0 ? (
            <div className="text-center py-12 px-4">
              <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
                <svg className="w-7 h-7 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
              </div>
              <p className="text-slate-600 font-semibold mb-1">Nenhum chat disponível</p>
              <p className="text-slate-400 text-sm">
                Peça para o usuário iniciar uma conversa com o bot e enviar <code className="px-1 py-0.5 rounded bg-slate-100 text-slate-700 font-mono">/start</code>.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100 rounded-xl ring-1 ring-slate-200 overflow-hidden">
              {chatsDisponiveis.map((c) => {
                const selected = String(c.chatId) === String(selectedChatId);
                const linkedToCurrent =
                  c.isLinked && (c.linkedUsers || []).some((u) => u.id === currentUserId);
                const fullName = [c.firstName, c.lastName].filter(Boolean).join(" ").trim() || "Sem nome";
                return (
                  <li key={c.chatId}>
                    <label
                      className={`flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors ${
                        selected ? "bg-violet-50/60" : "hover:bg-slate-50"
                      }`}
                    >
                      <input
                        type="radio"
                        name="telegram-chat"
                        checked={selected}
                        onChange={() => setSelectedChatId(c.chatId)}
                        className="mt-1 h-4 w-4 text-violet-600 focus:ring-violet-500 cursor-pointer"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className={`font-semibold truncate ${selected ? "text-violet-900" : "text-slate-800"}`}>
                            {fullName}
                          </p>
                          {c.userName && (
                            <span className="px-1.5 py-0.5 text-[10px] font-mono rounded bg-slate-100 text-slate-600">
                              @{c.userName}
                            </span>
                          )}
                          {linkedToCurrent && (
                            <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200">
                              Vinculado
                            </span>
                          )}
                        </div>
                        <div className="mt-0.5 flex items-center gap-2 text-xs text-slate-500 flex-wrap">
                          <span className="font-mono">{c.chatId}</span>
                          {c.latestMessageAt && (
                            <>
                              <span className="text-slate-300">·</span>
                              <span>Última msg: {formatarChatDate(c.latestMessageAt)}</span>
                            </>
                          )}
                        </div>
                        {c.latestMessageText && (
                          <p className="mt-1 text-[11px] text-slate-400 truncate italic">"{c.latestMessageText}"</p>
                        )}
                      </div>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/60 flex items-center justify-end gap-2 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 bg-white ring-1 ring-slate-200 hover:bg-slate-50 transition"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!selectedChatId || loading}
            className="px-5 py-2 rounded-xl text-sm font-semibold text-white shadow-lg shadow-purple-900/20 hover:shadow-purple-900/40 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
            style={{ background: "linear-gradient(135deg,#7c3aed,#6d28d9)" }}
          >
            Selecionar
          </button>
        </div>
      </div>
    </div>
  );
}
